import { TmcContextService } from './../../../../common/tmc-context.service';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, ContextChange, DialogService, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, UserProfileDialogService, WidgetStyle } from '@sdbn/common';


import { Station } from '../../../../client/model/station';
import { StationSet } from '../../../../client/model/station-set';
import { StructureSelectionService } from '../../../../common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { StructureSelection } from '../../../../common/structure-selection/structure-selection.model';
import { ReportingGroup, ReportingGroupListEntry } from '../reporting-group-administration.model';
import { ReportingGroupAdministrationService } from '../reporting-group-administration.service';


@Component({
    selector: 'app-reporting-group-administration',
    templateUrl: './reporting-group-administration.component.html',
    styleUrls: ['./reporting-group-administration.component.scss'],
    standalone: false
})
export class ReportingGroupAdministrationComponent extends AbstractWidgetComponent implements OnInit {

  stations = new Array<Station>();
  stationSets = new Array<StationSet>();

  listEntries: ReportingGroupListEntry[] = [];

  selected: ReportingGroupListEntry;
  selectedDetails = new ReportingGroup();
  workingCopyDetails = new ReportingGroup();

  responseMessages: ResponseMessage[];
  isLoading = false;
  detailsFormGroup: FormGroup;

  get cancelEnabled() {
    return !this.isLoading && this.detailsFormGroup != null && this.detailsFormGroup.dirty;
  }

  get saveEnabled() {
    return this.detailsFormGroup.dirty && this.detailsFormGroup.valid;
  }

  constructor(
    private tmcContextService: TmcContextService,
    private structureSelectionService: StructureSelectionService,
    private reportingGroupAdministrationService: ReportingGroupAdministrationService,
    private dialog: MatDialog,
    public translationService: TranslationService,
    public userProfileDialogService: UserProfileDialogService,
    private dialogService: DialogService
  ) {
    super();
    this.selectedDetails = this.createEmptyDetails();
    this.workingCopyDetails = this.createEmptyDetails();
    this.tmcContextService.updated.subscribe(
      () => this.updateListForView()
    );
    this.updateListForView();
    this.initializeDetailsForm();
  }

  ngOnInit() {
    this.load();
  }

  createTranslatedValuesOnList() {
    for (const supplementaryEquipment of this.listEntries) {
      supplementaryEquipment.translatedLabel = this.translationService.translate(supplementaryEquipment.nameDictionaryKey);
    }
  }

  private initializeDetailsForm() {
    this.detailsFormGroup = new FormGroup({
      sortIndex: new FormControl('', [Validators.required]),
      color1: new FormControl(null, []),
      backgroundColor1: new FormControl(null, []),
      color2: new FormControl(null, []),
      backgroundColor2: new FormControl(null, []),
      color3: new FormControl(null, []),
      backgroundColor3: new FormControl(null, [])
    });

    this.detailsFormGroup.valueChanges.subscribe(
      values => {
        if (this.detailsFormGroup.dirty) {
          this.workingCopyDetails.sortIndex = this.detailsFormGroup.controls.sortIndex.value;
          this.workingCopyDetails.color1 = this.detailsFormGroup.controls.color1.value;
          this.workingCopyDetails.backgroundColor1 = this.detailsFormGroup.controls.backgroundColor1.value;
          this.workingCopyDetails.color2 = this.detailsFormGroup.controls.color2.value;
          this.workingCopyDetails.backgroundColor2 = this.detailsFormGroup.controls.backgroundColor2.value;
          this.workingCopyDetails.color3 = this.detailsFormGroup.controls.color3.value;
          this.workingCopyDetails.backgroundColor3 = this.detailsFormGroup.controls.backgroundColor3.value;
        }
      }
    );
  }

  setSelected(message: ReportingGroupListEntry) {

    if (this.selected != null && this.selected.id === message.id) {
      return;
    }

    if (this.workingCopyDetails != null && this.detailsFormGroup.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.setSelectedWithoutCheck(message);
        }
      });

    } else {
      this.setSelectedWithoutCheck(message);
    }

  }

  setSelectedWithoutCheck(message: ReportingGroupListEntry) {
    this.selected = message;
    this.isLoading = true;
    this.responseMessages = [];

    this.reportingGroupAdministrationService.getReportinGroup(message.id).subscribe(details => {
      if (details != null) {
        this.selectedDetails = new ReportingGroup(details);
        this.workingCopyDetails = new ReportingGroup(details);
        this.updateListForView();
        this.resetFormValues();
      } else {
        this.clear();
      }
      this.isLoading = false;
    }, error => {
      this.isLoading = false;
      console.log(error);
    });
  }

  delete(message: ReportingGroupListEntry) {

    this.responseMessages = [];


    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.reporting-group-administration.delete.confirm-dialog.title',
      'tmc-yard.ui.admin.reporting-group-administration.delete.confirm-dialog.message'
    ).afterClosed().subscribe(confirm => {
      if (confirm) {
        this.isLoading = true;

        this.reportingGroupAdministrationService.deleteReportingGroup(message.id).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.isLoading = false;
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    });

  }

  load() {
    this.isLoading = true;
    this.responseMessages = [];
    this.reportingGroupAdministrationService.getReportinGroupListEntries().subscribe(entries => {

      this.listEntries = new Array<ReportingGroupListEntry>();
      for (const m of entries) {
        this.listEntries.push(new ReportingGroupListEntry(m));
      }

      this.createTranslatedValuesOnList();

      if (this.listEntries.length > 0) {
        if (this.selected == null) {
          this.setSelectedWithoutCheck(this.listEntries[0]);
        } else {
          const oldSelected = this.listEntries.find(s => s.id === this.selected.id);
          if (oldSelected != null) {
            this.setSelectedWithoutCheck(oldSelected);
          } else {
            this.setSelectedWithoutCheck(this.listEntries[0]);
          }
        }
      }

      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  addNew() {
    if (this.workingCopyDetails != null && this.detailsFormGroup.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.clear();
        }
      });

    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.workingCopyDetails != null && this.detailsFormGroup.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.reporting-group-administration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(confirm => {
        if (confirm) {
          this.discardChanges();
        }
      });

    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    if (this.workingCopyDetails.id === -1) {
      this.clear();
    } else {
      this.setSelectedWithoutCheck(this.selected);
    }
  }

  private clear() {
    this.responseMessages = [];
    this.selected = null;

    this.selectedDetails = this.createEmptyDetails();
    this.workingCopyDetails = new ReportingGroup((this.selectedDetails));
    this.updateListForView();
    this.resetFormValues();
  }

  private createEmptyDetails(): ReportingGroup {
    const details = new ReportingGroup();
    details.id = -1;

    return details;
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  public save() {
    this.isLoading = true;
    this.responseMessages = [];

    this.reportingGroupAdministrationService.saveReportingGroup(this.workingCopyDetails).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.selectedDetails = new ReportingGroup(this.workingCopyDetails);
        this.load();

      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as ReportingGroupListEntry);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as ReportingGroupListEntry);
  }

  public onDictionaryChanged() {
    this.detailsFormGroup.markAsDirty();
  }

  private resetFormValues() {
    const values: any = {};
    if (this.workingCopyDetails != null) {
      values.sortIndex = this.workingCopyDetails.sortIndex || 0;
      values.color1 = this.workingCopyDetails.color1;
      values.backgroundColor1 = this.workingCopyDetails.backgroundColor1;
      values.color2 = this.workingCopyDetails.color2;
      values.backgroundColor2 = this.workingCopyDetails.backgroundColor2;
      values.color3 = this.workingCopyDetails.color3;
      values.backgroundColor3 = this.workingCopyDetails.backgroundColor3;
    } else {
      values.sortIndex = 0;
      values.color1 = null;
      values.backgroundColor1 = null;
      values.color2 = null;
      values.backgroundColor2 = null;
      values.color3 = null;
      values.backgroundColor3 = null;
    }
    this.detailsFormGroup.reset(values);
  }

  public colorPickerChanged() {
    this.detailsFormGroup.markAsDirty();
  }

  public removeStation(station: Station) {
    const index = this.workingCopyDetails.stationIds.indexOf(station.id);
    this.workingCopyDetails.stationIds.splice(index, 1);
    this.updateListForView();
    this.detailsFormGroup.markAsDirty();
  }

  public addStation() {
    const structureSelection = new StructureSelection();

    // TODO structureSelectionService erweitern
    // selectStationEvent.includeStations = true;
    // selectStationEvent.includeWaitingareas = false;

    this.structureSelectionService.selectStation(structureSelection).subscribe(

      value => {

        if (value != null) {
          const selection: StructureSelection = value;

          if (selection.stationId != null) {
            if (this.workingCopyDetails.stationIds.indexOf(selection.stationId) === -1) {
              this.workingCopyDetails.stationIds.push(selection.stationId);
              this.updateListForView();
              this.detailsFormGroup.markAsDirty();
            }
          }
        }
      }
    );
  }


  public removeStationSet(stationSet: StationSet) {
    const index = this.workingCopyDetails.stationSetIds.indexOf(stationSet.id);
    this.workingCopyDetails.stationSetIds.splice(index, 1);
    this.updateListForView();
    this.detailsFormGroup.markAsDirty();
  }

  public addStationSet() {
    const structureSelection = new StructureSelection();

    // TODO structureSelectionService erweitern
    // selectStationEvent.includeStations = true;
    // selectStationEvent.includeWaitingareas = false;

    this.structureSelectionService.selectStationSet(structureSelection).subscribe(

      value => {

        if (value != null) {
          const selection: StructureSelection = value;

          if (selection.stationSetId != null) {
            if (this.workingCopyDetails.stationIds.indexOf(selection.stationSetId) === -1) {
              this.workingCopyDetails.stationSetIds.push(selection.stationSetId);
              this.updateListForView();
              this.detailsFormGroup.markAsDirty();
            }
          }
        }
      }
    );
  }

  private updateListForView() {

    const stations = new Array<Station>();
    const stationSets = new Array<StationSet>();

    if (this.tmcContextService.getYardConfiguration() != null) {
      const stationGroups = this.tmcContextService.getYardConfiguration().stationGroups;
      for (const sg of stationGroups) {
        for (const set of sg.stationSets) {

          if (this.workingCopyDetails.stationSetIds.indexOf(set.id) !== -1) {
            stationSets.push(set);
          }

          for (const station of set.stations) {
            if (this.workingCopyDetails.stationIds.indexOf(station.id) !== -1 && stations.findIndex(s => s.id === station.id) === -1) {
              stations.push(station);
            }
          }
        }
      }
    }

    this.stations = stations;
    this.stationSets = stationSets;
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
